<!-- THE AUS BONUS.

     Fires whenever AUSTRALIA or AUSTRIA is the correct answer, anywhere in the
     app. The brief asks for this to feel like the best moment in the game, so
     it takes the entire screen and nothing else competes with it.

     It dismisses ITSELF. A celebration with a Close button is a celebration
     you have to do paperwork for, and in a speed mode the clock is running.
     Tapping skips it early for the second and third time he sees it.

     WHAT CHANGED IN THE POLISH PASS, and why each one is a diagnostic row:

     · IT IS CHOREOGRAPHED, not simultaneous. Four blocks of type appearing on
       one frame is a slide; the flag lands, then the shout, then the line,
       then the badge, 80ms apart, and the badge throbs twice after everything
       else has settled. Two layers went in behind them — a single 60-degree
       sweep of rays and one shockwave — both bounded, both `aria-hidden`, both
       at 6% ink so the measured contrast on the yellow is untouched.

     · THE SKIP IS ON THE SCREEN. Tap-to-skip was documented in this comment
       and nowhere a player could see it: an invisible gesture with no visible
       alternative. Now it says so — not on the first sighting, where watching
       it is the point, and not after the fourth, where he has learnt it.

     · IT GETS SHORTER. It was identical on the hundredth sighting and the
       first. After three it runs at 78% length, which is the difference
       between a celebration and a toll gate in a mode where the score is a
       time.

     · REDUCED MOTION GETS A CROSSFADE, not a 1ms swap. See app.css. -->
<template>
  <div v-if="show" class="aus" role="status" aria-live="assertive" @click="finish">
    <div class="rays" aria-hidden="true"></div>
    <div class="shock" aria-hidden="true"></div>
    <div class="flag" aria-hidden="true">{{ flag }}</div>
    <p class="shout">AUS-TIN!</p>
    <p class="line">That one's got your name on it.</p>
    <p class="double">DOUBLE POINTS</p>
    <p v-if="hint" class="skip">Tap to skip</p>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { haptic } from '../core/util.js';
import { announce, showUntilLearned } from '../core/ui';

const props = defineProps({
  /** Flip to true to fire it. Flipping back is not needed — it clears itself. */
  when: { type: Boolean, default: false },
  /** Austria gets its own flag, because getting that right deserves the credit. */
  flag: { type: String, default: '\u{1F1E6}\u{1F1FA}' },
  ms: { type: Number, default: 1900 },
});

const emit = defineEmits(['done']);
const show = ref(false);
const hint = ref(false);
let timer = null;

function finish() {
  if (!show.value) return;
  clearTimeout(timer);
  timer = null;
  show.value = false;
  emit('done');
}

watch(
  () => props.when,
  (on) => {
    if (!on) return;
    show.value = true;
    haptic('success');
    announce("AUS-TIN! That one's got your name on it. Double points.");

    // THE LONG LOOP, in two counters because they answer two questions.
    //
    // `showUntilLearned` returns true for the first N calls on a key and false
    // after, so `aus.first` is "has he ever seen this" and `aus.skip-hint` is
    // "is the hint still earning its place". The hint is therefore off on
    // sighting one, on for two, three and four, and off again from five.
    // Reading it as a pair is the whole trick: a hint on the very first
    // sighting is an instruction to skip the thing you were about to enjoy.
    const first = showUntilLearned('aus.first', 1);
    const stillHinting = showUntilLearned('aus.skip-hint', 4);
    hint.value = !first && stillHinting;

    // And it shortens once it is familiar. Never below 78% — the choreography
    // above needs ~1.1s to finish landing, and a celebration cut off mid-beat
    // reads as a bug rather than as brevity.
    const full = showUntilLearned('aus.full-length', 3);
    clearTimeout(timer);
    timer = setTimeout(finish, full ? props.ms : Math.round(props.ms * 0.78));
  },
);

onUnmounted(() => clearTimeout(timer));
</script>
